/**
 * Batch Export and batch Run workflow on the Library's selection (1.2.0), against POST /api/v1/exports/batch and
 * POST /api/v1/automations/:id/runs/batch, and the per-asset preflight (GET /api/v1/preflight with assetIds; key:
 * queryKeys.batchPreflight(params)). Owned by the batch stream (X) from step 0 on; the signatures are final.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BatchExportBody, BatchPreflightResult, BatchResult, BatchRunBody, RightsAck } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

/** The per-asset preflight of a batch dialog: `params` are the preflight's query (kind, assetIds, the dialog's choices); null waits. */
export function useBatchPreflight(params: Record<string, string | null | undefined> | null) {
  const search = params ? new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => Boolean(e[1]))).toString() : '';
  return useQuery({
    queryKey: queryKeys.batchPreflight(search),
    queryFn: () => api<BatchPreflightResult>(`/api/v1/preflight?${search}`),
    enabled: Boolean(params),
  });
}

/** Starts the exports; the result says what started and what was skipped (and why). */
export function useBatchExport() {
  const qc = useQueryClient();
  return useMutation({
    // 1.8.0: `rights` (the confirm and override answers) with the start.
    mutationFn: (body: BatchExportBody & { rights?: RightsAck }) => api<BatchResult>('/api/v1/exports/batch', { method: 'POST', json: body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exports'] }),
  });
}

/** Starts one manual run of a workflow per asset. */
export function useBatchRunWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, body }: { workflowId: string; body: BatchRunBody & { rights?: RightsAck } }) =>

      api<BatchResult>(`/api/v1/automations/${workflowId}/runs/batch`, { method: 'POST', json: body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automation-runs'] }),
  });
}
